import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";

function CompareSlider({ before, after, name, weeks, description, idx }) {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);

  const handleMove = (clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      data-testid={`before-after-${idx}`}
      className="bg-white border border-border"
    >
      <div
        ref={ref}
        className="relative aspect-[4/5] overflow-hidden select-none cursor-ew-resize"
        onMouseMove={(e) => e.buttons === 1 && handleMove(e.clientX)}
        onTouchMove={(e) => handleMove(e.touches[0].clientX)}
        onClick={(e) => handleMove(e.clientX)}
      >
        <img src={after} alt={`${name} depois`} className="absolute inset-0 w-full h-full object-cover" />
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        >
          <img src={before} alt={`${name} antes`} className="w-full h-full object-cover" />
        </div>
        <span className="absolute top-3 left-3 px-2 py-1 text-[10px] tracking-[0.2em] uppercase font-bold bg-onyx text-alabaster">
          Antes
        </span>
        <span className="absolute top-3 right-3 px-2 py-1 text-[10px] tracking-[0.2em] uppercase font-bold bg-gold text-onyx">
          Depois
        </span>
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-gold pointer-events-none"
          style={{ left: `${pos}%` }}
        >
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gold flex items-center justify-center shadow-lg">
            <MoveHorizontal size={18} className="text-onyx" />
          </span>
        </div>
      </div>
      {(name || weeks || description) && (
        <div className="p-5">
          <div className="flex items-baseline justify-between mb-1">
            {name && <p className="font-semibold text-onyx">{name}</p>}
            {weeks && <p className="text-xs text-gold tracking-wide">{weeks}</p>}
          </div>
          {description && <p className="text-sm text-onyx/70">{description}</p>}
        </div>
      )}
    </motion.div>
  );
}

export default function BeforeAfter({ data }) {
  const items = data.items || [];
  return (
    <section data-testid="before-after-section" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="overline mb-4" data-testid="before-after-overline">{data.overline}</p>
          <h2 data-testid="before-after-title" className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-onyx">
            {data.title}
          </h2>
          <p className="mt-4 text-onyx/70">{data.subtitle}</p>
          <p className="mt-2 text-xs text-onyx/40 tracking-wide">Arraste para comparar →</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {items.map((it, i) => (
            <CompareSlider key={i} idx={i} {...it} />
          ))}
        </div>
      </div>
    </section>
  );
}
